import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { FieldWrapper } from "./FieldWrapper";

interface DatePickerProps {
  label?: string;
  helper?: string;
  error?: string;
  required?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  id?: string;
  maxDate?: Date;
  minDate?: Date;
}

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const CURRENT_YEAR = new Date().getFullYear();

function pad(n: number) {
  return n.toString().padStart(2, "0");
}

function parseDateString(str?: string): Date {
  if (!str) return new Date();
  const direct = new Date(str);
  if (!isNaN(direct.getTime())) return direct;
  const parts = str.trim().split(/[\s-]+/);
  if (parts.length === 3) {
    const day = parseInt(parts[0], 10);
    const monthIdx = MONTHS.findIndex((m) => m.toLowerCase() === parts[1].slice(0, 3).toLowerCase());
    const yr = parseInt(parts[2], 10);
    if (!isNaN(day) && monthIdx !== -1 && !isNaN(yr)) {
      return new Date(yr, monthIdx, day);
    }
  }
  return new Date();
}

export default function DatePicker({
  label,
  helper,
  error,
  required,
  value,
  onChange,
  placeholder = "Select date",
  id,
  maxDate,
  minDate,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => {
    const parsed = parseDateString(value);
    if (!value && maxDate && parsed.getTime() > maxDate.getTime()) {
      return new Date(maxDate);
    }
    return parsed;
  });
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && value) {
      setViewDate(parseDateString(value));
    }
  }, [open, value]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const cells: (number | null)[] = [
    ...Array.from({ length: firstDay }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  function changeMonth(delta: number) {
    setViewDate((d) => new Date(d.getFullYear(), d.getMonth() + delta, 1));
  }

  function selectDay(day: number) {
    const selected = new Date(year, month, day);
    const formatted = `${pad(selected.getDate())} ${MONTHS[selected.getMonth()]} ${selected.getFullYear()}`;
    onChange?.(formatted);
    setOpen(false);
  }

  const maxYear = maxDate ? maxDate.getFullYear() : CURRENT_YEAR + 10;
  const minYear = minDate ? minDate.getFullYear() : 1920;
  const availableYears = Array.from(
    { length: Math.max(1, maxYear - minYear + 1) },
    (_, i) => maxYear - i
  );

  return (
    <FieldWrapper label={label} helper={helper} error={error} required={required} htmlFor={id}>
      <div ref={containerRef} className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={`flex h-[42px] w-full items-center justify-between rounded-lg border bg-white pl-3.5 pr-3 text-sm focus:outline-none focus:ring-2 ${
            error
              ? "border-danger focus:ring-danger/30"
              : "border-slate-300 focus:border-primary-500 focus:ring-primary-500/30"
          } ${value ? "text-ink" : "text-ink-muted"} cursor-pointer`}
        >
          <span>{value || placeholder}</span>
          <CalendarIcon className="h-[18px] w-[18px] text-ink-muted" />
        </button>

        {open && (
          <div className="absolute z-50 mt-2 w-72 rounded-lg border border-line bg-white p-3 shadow-dropdown animate-fadeIn">
            <div className="mb-2.5 flex items-center justify-between gap-1">
              <button
                type="button"
                onClick={() => changeMonth(-1)}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-ink-secondary hover:bg-slate-100 cursor-pointer"
                aria-label="Previous month"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <div className="flex items-center gap-1.5">
                <select
                  value={month}
                  onChange={(e) => setViewDate(new Date(year, Number(e.target.value), 1))}
                  className="h-7 rounded border border-slate-200 bg-slate-50 px-1.5 text-xs font-semibold text-ink focus:border-primary-500 focus:outline-none cursor-pointer"
                  aria-label="Select month"
                >
                  {MONTHS.map((m, idx) => (
                    <option key={m} value={idx}>
                      {MONTH_NAMES[idx]}
                    </option>
                  ))}
                </select>
                <select
                  value={year}
                  onChange={(e) => setViewDate(new Date(Number(e.target.value), month, 1))}
                  className="h-7 rounded border border-slate-200 bg-slate-50 px-1.5 text-xs font-semibold text-ink focus:border-primary-500 focus:outline-none cursor-pointer"
                  aria-label="Select year"
                >
                  {availableYears.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                onClick={() => changeMonth(1)}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-ink-secondary hover:bg-slate-100 cursor-pointer"
                aria-label="Next month"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center">
              {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d, i) => (
                <span key={i} className="pb-1 text-xs font-medium text-ink-muted">
                  {d}
                </span>
              ))}
              {cells.map((day, i) => {
                if (day === null) return <span key={i} />;
                const date = new Date(year, month, day);
                const isToday = date.getTime() === today.getTime();
                const isSelected = value === `${pad(day)} ${MONTHS[month]} ${year}`;
                const maxThreshold = maxDate ? new Date(maxDate).setHours(23, 59, 59, 999) : null;
                const minThreshold = minDate ? new Date(minDate).setHours(0, 0, 0, 0) : null;
                const isDayDisabled =
                  (maxThreshold !== null && date.getTime() > maxThreshold) ||
                  (minThreshold !== null && date.getTime() < minThreshold);

                return (
                  <button
                    key={i}
                    type="button"
                    disabled={isDayDisabled}
                    onClick={() => !isDayDisabled && selectDay(day)}
                    className={`flex h-8 w-full items-center justify-center rounded-md text-[13px] transition-colors ${
                      isDayDisabled
                        ? "opacity-25 cursor-not-allowed text-slate-400"
                        : isSelected
                          ? "bg-primary-500 text-white cursor-pointer"
                          : isToday
                            ? "bg-primary-50 font-semibold text-primary-600 hover:bg-primary-100 cursor-pointer"
                            : "text-ink hover:bg-slate-100 cursor-pointer"
                    }`}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </FieldWrapper>
  );

}